import { useState, type ReactNode } from "react";
import { IconRail } from "./IconRail";
import { BottomTabBar } from "./BottomTabBar";
import { TopBar } from "./TopBar";
import { ScrollContainerContext } from "./ScrollContainer";
import { APP_SCROLL_ID, SCROLL_RESTORATION_ATTRIBUTE } from "@/lib/scroll-restoration";

export interface AppShellProps {
  children: ReactNode;
  rightRail?: ReactNode;
}

/**
 * DESIGN.md §8.1: expandable IconRail (≥768) / BottomTabBar (<768),
 * plus a TopBar below 1280. The shell fills the viewport and only the main
 * column scrolls, so the sidebar/rail stay fixed in place like the reference.
 */
export function AppShell({ children, rightRail }: AppShellProps) {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);

  return (
    <div className="flex h-screen overflow-hidden bg-bg">
      <IconRail />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar />
        <div className="flex min-h-0 flex-1">
          {/* Not a <main>: every page component supplies its own, so this stays a plain scroll container. */}
          <ScrollContainerContext.Provider value={scrollElement}>
            <div
              ref={setScrollElement}
              data-scroll-container=""
              {...{ [SCROLL_RESTORATION_ATTRIBUTE]: APP_SCROLL_ID }}
              className="min-w-0 flex-1 overflow-y-auto pb-14 md:pb-0"
            >
              {children}
            </div>
          </ScrollContainerContext.Provider>
          {rightRail}
        </div>
      </div>
      <BottomTabBar />
    </div>
  );
}
